import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { JuniorLayout } from '../../components/layout/JuniorLayout';
import { ActivityShell } from '../../components/activity/ActivityShell';
import { AtrapaRitmoLobby } from '../../components/atrapaRitmo/AtrapaRitmoLobby';
import { AtrapaRitmoStage } from '../../components/atrapaRitmo/AtrapaRitmoStage';
import {
  ATRAPA_RITMO_DIFFICULTIES,
  createAtrapaRitmoState,
  startAtrapaRitmo,
  tickAtrapaRitmo,
  tryHitRhythmBubble,
  type AtrapaRitmoDifficulty,
  type AtrapaRitmoState,
} from '../../lib/game/atrapaRitmoEngine';
import { buildPercusionBucketRects, PERCUSION_CORPORAL } from '../../lib/percusionCorporal';
import { preloadPercusionSounds, playPercusionSound } from '../../lib/usePercusionSounds';
import { RequireJuniorSession } from '../../components/auth/RequireJuniorSession';

const UI_FRAME_MS = 50;

function AtrapaRitmoPageInner() {
  const [difficulty, setDifficulty] = useState<AtrapaRitmoDifficulty>(
    ATRAPA_RITMO_DIFFICULTIES[0]!
  );
  const [game, setGame] = useState<AtrapaRitmoState>(() =>
    createAtrapaRitmoState(ATRAPA_RITMO_DIFFICULTIES[0]!)
  );
  const [uiTick, setUiTick] = useState(0);

  const gameRef = useRef(game);
  gameRef.current = game;

  const lastTs = useRef(0);
  const lastUiTs = useRef(0);
  const playfieldHeightRef = useRef(400);
  const playfieldWidthRef = useRef(800);
  const bucketRectsRef = useRef(buildPercusionBucketRects(800, 420));

  const playing = game.phase === 'playing';
  const inRound = game.phase === 'playing' || game.phase === 'finished';

  useEffect(() => {
    preloadPercusionSounds(PERCUSION_CORPORAL.map((p) => p.soundPath));
  }, []);

  const rebuildBucketRects = useCallback(() => {
    bucketRectsRef.current = buildPercusionBucketRects(
      playfieldWidthRef.current,
      playfieldHeightRef.current
    );
  }, []);

  const handlePlayfieldSizeChange = useCallback(
    (w: number, h: number) => {
      if (playfieldWidthRef.current === w && playfieldHeightRef.current === h) return;
      playfieldWidthRef.current = w;
      playfieldHeightRef.current = h;
      rebuildBucketRects();
    },
    [rebuildBucketRects]
  );

  useEffect(() => {
    if (!playing) return;

    let raf: number;
    const loop = (ts: number) => {
      const dt = lastTs.current ? Math.min(ts - lastTs.current, 32) : 16;
      lastTs.current = ts;

      const bucketRects = bucketRectsRef.current.length
        ? bucketRectsRef.current
        : (() => {
            rebuildBucketRects();
            return bucketRectsRef.current;
          })();

      let next = tickAtrapaRitmo(
        gameRef.current,
        dt,
        playfieldHeightRef.current,
        bucketRects,
        ts
      );

      gameRef.current = next;

      if (ts - lastUiTs.current >= UI_FRAME_MS || next.phase !== 'playing') {
        lastUiTs.current = ts;
        setGame(next);
        setUiTick((n) => n + 1);
      }

      if (next.phase === 'playing') {
        raf = requestAnimationFrame(loop);
      } else {
        setGame(next);
      }
    };

    lastTs.current = 0;
    lastUiTs.current = 0;
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, rebuildBucketRects]);

  const handleTapPercusion = useCallback((percusionId: string) => {
    if (gameRef.current.phase !== 'playing') return;
    const item = PERCUSION_CORPORAL.find((p) => p.id === percusionId);
    if (item) playPercusionSound(item.soundPath);

    const next = tryHitRhythmBubble(
      gameRef.current,
      percusionId,
      bucketRectsRef.current,
      performance.now()
    );
    if (next !== gameRef.current) {
      gameRef.current = next;
      setGame(next);
      setUiTick((n) => n + 1);
    }
  }, []);

  const handleStart = () => {
    const next = startAtrapaRitmo(createAtrapaRitmoState(difficulty));
    gameRef.current = next;
    setGame(next);
  };

  const handlePlayAgain = () => {
    const next = startAtrapaRitmo(createAtrapaRitmoState(game.difficulty));
    gameRef.current = next;
    setGame(next);
  };

  const handleChangeLevel = () => {
    const next = createAtrapaRitmoState(difficulty);
    gameRef.current = next;
    setGame(next);
  };

  const activeFeedback = useMemo(() => {
    void uiTick;
    return game.feedbackUntil > performance.now() ? game.lastFeedback : null;
  }, [game.feedbackUntil, game.lastFeedback, uiTick]);

  const beatPhase = useMemo(() => {
    void uiTick;
    const elapsed = game.difficulty.durationMs - game.timeLeftMs;
    const msPerBeat = 60_000 / game.difficulty.bpm;
    return (elapsed / msPerBeat) % 1;
  }, [game.timeLeftMs, game.difficulty.durationMs, game.difficulty.bpm, uiTick]);

  return (
    <JuniorLayout immersive={inRound}>
      <ActivityShell
        title="Atrapa el ritmo"
        subtitle="Toca cuando caigan las fichas"
        backTo="/ritmo"
        backLabel="Ritmo"
        immersive={inRound}
        lockViewport={inRound}
      >
        {game.phase === 'lobby' && (
          <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center px-2">
            <AtrapaRitmoLobby
              selected={difficulty}
              onSelect={(d) => {
                setDifficulty(d);
                const next = createAtrapaRitmoState(d);
                gameRef.current = next;
                setGame(next);
              }}
              onStart={handleStart}
            />
          </div>
        )}

        {inRound && (
          <AtrapaRitmoStage
            bubbles={game.bubbles}
            difficulty={game.difficulty}
            timeLeftMs={game.timeLeftMs}
            score={game.score}
            streak={game.streak}
            feedback={activeFeedback}
            beatPhase={beatPhase}
            bucketFlashUntil={game.bucketFlashUntil}
            frozen={game.phase === 'finished'}
            results={
              game.phase === 'finished'
                ? {
                    score: game.score,
                    hits: game.hits,
                    misses: game.misses,
                    maxStreak: game.maxStreak,
                  }
                : null
            }
            onPlayAgain={handlePlayAgain}
            onChangeLevel={handleChangeLevel}
            onPlayfieldSizeChange={handlePlayfieldSizeChange}
            onTapPercusion={handleTapPercusion}
          />
        )}
      </ActivityShell>
    </JuniorLayout>
  );
}

export default function AtrapaRitmoPage() {
  return (
    <RequireJuniorSession>
      <AtrapaRitmoPageInner />
    </RequireJuniorSession>
  );
}
